import { useState } from "react"
import { AttendanceStatus } from "../../types/attendanceStatus"
import { defaultAvatar } from "@/utils/svgIcons"

type InternalAttendanceStatusForFaireLAppel =
   | "present"
   | "late"
   | "late_expected"
   | "absent"
   | "absent_expected"

export type OnChangeFnParamsType = {
   newStatus: AttendanceStatus
   newExpected: boolean
}

export type OnChangeFnType = ({ newStatus, newExpected }: OnChangeFnParamsType) => void

interface PhotoYoungProps {
   status: AttendanceStatus
   statusExpected: boolean
   originalStatus: AttendanceStatus
   originalExpected: boolean
   fullname: string
   avatar: string
   onSelect: () => void
   onChange: OnChangeFnType
}

const getNextAttendanceStatus = (
   currentStatus: InternalAttendanceStatusForFaireLAppel,
   originalStatus: InternalAttendanceStatusForFaireLAppel,
): InternalAttendanceStatusForFaireLAppel => {
   
   let pattern: Record<InternalAttendanceStatusForFaireLAppel, InternalAttendanceStatusForFaireLAppel> = {} as any

   if (originalStatus === "present") {
      pattern = {
         present: "late",
         late: "late_expected",
         late_expected: "absent",
         absent: "absent_expected",
         absent_expected: "present",
      }
   }
   else {
      pattern = {
         [originalStatus]: "present",
         present: originalStatus,
      } as Partial<Record<InternalAttendanceStatusForFaireLAppel, InternalAttendanceStatusForFaireLAppel>> as any
   } 

   return pattern[currentStatus]
}

export const getInternalStatus = (
   status: AttendanceStatus,
   is_current_attendance_expected: boolean,
): InternalAttendanceStatusForFaireLAppel => {
   if (status === "present") return "present"
   if (status === "external") return "absent_expected"
   return (status + (is_current_attendance_expected ? "_expected" : "")) as any
}

const getExternalStatus = (internStatus: InternalAttendanceStatusForFaireLAppel): OnChangeFnParamsType => {
   if (internStatus === "present") return { newStatus: "present", newExpected: true }
   if (internStatus === "late") return { newStatus: "late", newExpected: false }
   if (internStatus === "late_expected") return { newStatus: "late", newExpected: true }
   if (internStatus === "absent") return { newStatus: "absent", newExpected: false }
   if (internStatus === "absent_expected") return { newStatus: "absent", newExpected: true }
   throw new Error("Invalid status")
}

export const getStatusStyle = (status: InternalAttendanceStatusForFaireLAppel) => {
   switch (status) {
      case "present":
         return {
            border: "border-success",
            icon: null,
         }
      case "late":
         return {
            border: "border-warning",
            icon: (
               <div className="badge badge-warning badge-lg flex items-center gap-1 zoom-13">
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                     />
                  </svg>
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
                     />
                  </svg>
               </div>
            ),
         }
      case "late_expected":
         return {
            border: "border-warning",
            icon: (
               <div className="badge badge-lg flex items-center gap-1 zoom-13">
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                     />
                  </svg>
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
                     />
                  </svg>
               </div>
            ),
         }
      case "absent":
         return {
            border: "border-error",
            icon: (
               <div className="badge badge-error badge-lg flex items-center gap-1 zoom-13">
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                     />
                  </svg>
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
                     />
                  </svg>
               </div>
            ),
         }
      case "absent_expected":
         return {
            border: "border-error",
            icon: (
               <div className="badge badge-lg flex items-center gap-1 zoom-13">
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                     />
                  </svg>
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     fill="none"
                     viewBox="0 0 24 24"
                     strokeWidth={1.5}
                     stroke="currentColor"
                     className="size-4"
                  >
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
                     />
                  </svg>
               </div>
            ),
         }
   }
}

export function OneYoungForRowCall({
   status,
   statusExpected,
   originalStatus,
   originalExpected,
   fullname,
   avatar,
   onSelect,
   onChange,
}: PhotoYoungProps) {
   const [internStatus, setInternStatus] = useState<InternalAttendanceStatusForFaireLAppel>(
      getInternalStatus(status, statusExpected),
   )

   const handleClick = () => {
      const originalInternStatus = getInternalStatus(originalStatus, originalExpected)
      const newStatus = getNextAttendanceStatus(internStatus, originalInternStatus)
      setInternStatus(newStatus)
      onChange(getExternalStatus(newStatus))
      onSelect()
   }

   const style = getStatusStyle(internStatus)

   return (
      <div className="relative aspect-square cursor-pointer group">
         <div className={`absolute inset-0 rounded-lg border-4 transition-colors ${style.border}`}>
            {avatar && <img
               onClick={handleClick}
               src={avatar}
               alt={fullname}
               className="w-full h-full object-cover rounded-md"
               loading="lazy"
            />}
            {!avatar && (
               <div
                  onClick={handleClick}
                  className="w-full h-full object-cover rounded-md">
                  {defaultAvatar}
               </div>
            )}
            <div onClick={handleClick} className="absolute top-2 right-3 flex gap-1 ">
               {style.icon}
            </div>
            <div
               onClick={() => {
                  onSelect()
               }}
               className="absolute bottom-0 left-0 right-0 p-2 bg-black/50 text-white rounded-b flex flex-row items-center"
            >
               <div className="text-sm font-medium truncate">{fullname}</div>
            </div>
         </div>
      </div>
   )
}
